import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './ProductDetail.css';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fromCategory = location.state?.fromCategory || null;
    const fromCategoryTitle = location.state?.fromCategoryTitle || 'Products';

    const goBack = () => {
        if (fromCategory) {
            navigate('/products', { state: { restoreCategory: fromCategory } });
        } else {
            navigate('/products');
        }
    };
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const thumbnailRef = useRef(null);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/products/${id}`);
                if (!response.ok) {
                    navigate('/products');
                    return;
                }
                const data = await response.json();
                setProduct(data);
            } catch (error) {
                console.error('Error fetching product:', error);
                navigate('/products');
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id, navigate]);

    const handleQuantityChange = (increment) => {
        if (increment) {
            setQuantity(prev => prev + 1);
        } else if (quantity > 1) {
            setQuantity(prev => prev - 1);
        }
    };

    if (loading) {
        return <div className="loading-page">Loading product details...</div>;
    }

    if (!product) {
        return null;
    }

    return (
        <div className="product-detail-page">
            <div className="container">
                {/* Breadcrumb */}
                <nav className="breadcrumb">
                    <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
                    <span className="breadcrumb-separator">›</span>
                    <span onClick={goBack} className="breadcrumb-link">{fromCategoryTitle}</span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-current">{product.name}</span>
                </nav>

                {(() => {
                    const allImages = product.images?.length > 0 ? product.images : (product.image ? [product.image] : []);
                    const safeIndex = Math.min(selectedImage, allImages.length - 1);
                    const currentSrc = allImages[safeIndex];
                    return (
                        <div className="product-detail-content">
                            {/* Product Images */}
                            <div className="product-images">
                                <div className="main-image">
                                    {currentSrc ? (
                                        <img
                                            src={currentSrc}
                                            alt={product.name}
                                            className="main-product-img"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div className="image-placeholder-large">
                                            <span className="category-label">{product.category.toUpperCase()}</span>
                                            <div className="image-text">{product.brand}</div>
                                        </div>
                                    )}

                                    {/* Left / Right navigation arrows */}
                                    {allImages.length > 1 && (
                                        <>
                                            <button
                                                className="img-nav-btn img-nav-prev"
                                                onClick={() => setSelectedImage(i => Math.max(0, i - 1))}
                                                disabled={safeIndex === 0}
                                                aria-label="Previous image"
                                            >‹</button>
                                            <button
                                                className="img-nav-btn img-nav-next"
                                                onClick={() => setSelectedImage(i => Math.min(allImages.length - 1, i + 1))}
                                                disabled={safeIndex === allImages.length - 1}
                                                aria-label="Next image"
                                            >›</button>
                                            <div className="img-nav-counter">{safeIndex + 1} / {allImages.length}</div>
                                        </>
                                    )}

                                    {!product.inStock && (
                                        <div className="out-of-stock-overlay">
                                            <span>OUT OF STOCK</span>
                                        </div>
                                    )}
                                </div>

                                {/* Thumbnails */}
                                {allImages.length > 1 && (
                                    <div className="thumbnail-strip">
                                        {allImages.length > 3 && (
                                            <button className="thumb-nav-btn" onClick={() => thumbnailRef.current?.scrollBy({ left: -96, behavior: 'smooth' })}>‹</button>
                                        )}
                                        <div className="thumbnail-images" ref={thumbnailRef}>
                                            {allImages.map((img, index) => (
                                                <div
                                                    key={index}
                                                    className={`thumbnail ${safeIndex === index ? 'active' : ''}`}
                                                    onClick={() => setSelectedImage(index)}
                                                >
                                                    <img
                                                        src={img}
                                                        alt={`View ${index + 1}`}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {allImages.length > 3 && (
                                            <button className="thumb-nav-btn" onClick={() => thumbnailRef.current?.scrollBy({ left: 96, behavior: 'smooth' })}>›</button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Product Info */}
                            <div className="product-info">
                                <h1 className="product-title">{product.name}</h1>
                                <div className="product-brand">Brand: {product.brand}</div>

                                <div className="product-price">
                                    <span className="price">$ {product.price}</span>
                                    <span className={`stock-status ${product.inStock ? 'in-stock' : product.out_for_rent ? 'out-for-rent' : 'out-of-stock'}`}>
                                        {product.inStock ? 'In Stock' : product.out_for_rent ? 'Out for Rent' : 'Out of Stock'}
                                    </span>
                                </div>

                                {product.category === 'racquets' && (product.rentalFee != null || product.item_condition || product.swing_weight || product.weight_type || product.weight || product.balance_point || product.grommet_system) && (
                                    <div className="racquet-info-panel">
                                        <h3>Specification</h3>
                                        {product.rentalFee != null && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Rental (10 days)</span>
                                                <span className="racquet-info-value">$ {product.rentalFee}</span>
                                            </div>
                                        )}
                                        {product.item_condition && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Condition</span>
                                                <span className="racquet-info-value">{product.item_condition}</span>
                                            </div>
                                        )}
                                        {product.swing_weight && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Swing Weight</span>
                                                <span className="racquet-info-value">{product.swing_weight}</span>
                                            </div>
                                        )}
                                        {product.weight_type && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Weight Type</span>
                                                <span className="racquet-info-value">{product.weight_type}</span>
                                            </div>
                                        )}
                                        {product.weight && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Weight</span>
                                                <span className="racquet-info-value">{product.weight}</span>
                                            </div>
                                        )}
                                        {product.balance_point && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Balance Point</span>
                                                <span className="racquet-info-value">{product.balance_point}</span>
                                            </div>
                                        )}
                                        {product.grommet_system && (
                                            <div className="racquet-info-row">
                                                <span className="racquet-info-label">Grommet System</span>
                                                <span className="racquet-info-value">{product.grommet_system}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="product-description">
                                    <h3>Description</h3>
                                    <p>{product.description}</p>
                                </div>


                                {/* Quantity and Add to Cart */}
                                <div className="purchase-section">
                                    {/* <div className="quantity-selector">
                                <label htmlFor="quantity">Quantity:</label>
                                <div className="quantity-controls">
                                    <button 
                                        onClick={() => handleQuantityChange(false)}
                                        disabled={quantity <= 1}
                                    >
                                        −
                                    </button>
                                    <span className="quantity-display">{quantity}</span>
                                    <button 
                                        onClick={() => handleQuantityChange(true)}
                                        disabled={!product.inStock}
                                    >
                                        +
                                    </button>
                                </div>
                            </div> */}

                                    <div className="action-buttons">
                                        <button
                                            className="contact-btn"
                                            onClick={() => navigate('/contact')}
                                        >
                                            Contact for Purchase
                                        </button>
                                        <button
                                            className="back-btn"
                                            onClick={goBack}
                                        >
                                            ← Back to {fromCategoryTitle}
                                        </button>
                                    </div>
                                </div>

                                {/* Additional Info */}
                                <div className="additional-info">
                                    <div className="info-section">
                                        <h4>🚚 Pickup Information</h4>
                                        <p>All products are available for pickup at our physical location. Contact us for availability and arrangements.</p>
                                    </div>
                                    <div className="info-section">
                                        <h4>💬 Need Help?</h4>
                                        <p>Our experts are available to help you choose the right equipment for your game.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default ProductDetail;